import { SysnotificationService } from './sysnotification.service';
import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Socket, SocketIoConfig } from 'ngx-socket-io';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AgentData, sessionStatus } from '../models/user.interface';
import { AuthService } from './auth.service';
import { LocalService } from './local.service';
import { SessionService } from './session.service';

@Injectable({
  providedIn: 'root'
})
export class AgentService {
  private SocketAgent: Socket;
  public AllAvailableAgentsList: Array<any> = []; // All available agents by all topics
  public topicIds: Array<any> = [];
  @Output() transferContactModal: EventEmitter<any> = new EventEmitter();
  @Output() cancelFeedbackUpdate: EventEmitter<boolean> = new EventEmitter();
  @Output() saveFeedBackEvent: EventEmitter<boolean> = new EventEmitter();

  private _agentSessionStatus: BehaviorSubject<sessionStatus> = new BehaviorSubject<sessionStatus>({ id: 5, name: "undefined" });
  public agentSessionStatus$ = this._agentSessionStatus.asObservable();
  private _enableStartChatButton: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public enableStartChatButton$ = this._enableStartChatButton.asObservable();
  private _companyTopicList: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  public companyTopicList$ = this._companyTopicList.asObservable();
  // Fires on each socket `connect` so views can re-join rooms and re-query status after a (re)connect.
  private _onConnect: Subject<void> = new Subject<void>();
  public onConnect$ = this._onConnect.asObservable();

  URL: string;
  ADMINISTRATION_URL: string;
  _editing = false;
  room: string; //AGENT ROOM
  currentCompanyId: number;
  agentsResolved = [];
  private activeAgentStatus: string;
  private _cachedAgent: any = null;
  private _cachedAgentId: number = null;
  private _hasConnectedOnce: boolean = false;
  private worker: Worker;
  private _refreshTimer: any;

  constructor(
    private sessionService: SessionService,
    private http: HttpClient,
    private _authService: AuthService,
    private localService: LocalService,
    private router: Router,
    private sysNotification: SysnotificationService
  ) {
    this.URL = `${environment.baseServerURL}/security`;
    this.ADMINISTRATION_URL = `${environment.baseServerURL}/administration`;
    this.currentCompanyId = this._authService.user.company.id;
    this.initService();
  }

  buildURL(subPath: string) {
    return `${this.URL}/user/agent/${subPath}`;
  }

  private initService(): void {
    //Connect to socket server
    this.onUserConnected();
    //SET OFFLINE LISTENER
    this.setAgentOffline();
    //CHANGE COMPANY LISTENER
    this.changeCompany();
    //AGENT PERMISSIONS
    this.getAgentPermissions();
  }

  availableAgentsByTopic(topic: number) {
    this.emit('availableAgents', {
      room: `availableAgents-${this._authService.user.company.id}`,
      companyId: this._authService.user.company.id,
      queueType: topic
    });
  }

  allAgentsAvailable(roomReference?) {
    this.AllAvailableAgentsList = [];
    this.emit('allAvailableAgents', {
      room: roomReference || `allAvailableAgents-admin-${this._authService.user.company.id}`,
      companyId: this._authService.user.company.id,
    });
  }

  private loadTopics(): void {
    this.getTopics().subscribe(topicList => {
      this.topicIds = topicList.map((topic: any) => topic.id);
      this._companyTopicList.next(topicList);
    });
  }

  refreshAllAvailable() {
    clearTimeout(this._refreshTimer);
    this._refreshTimer = setTimeout(() => { this.allAgentsAvailable(); }, 500);
  }

  onUserConnected() {
    const email = this._authService.user.email;
    this.room = this._authService.user.email;
    const wssconfig: SocketIoConfig = {
      url: `${environment.socketsURL}/agent`,
      options: {
        transports: ["websocket"],
        reconnection: true,
        autoConnect: true
      }
    };
    this.SocketAgent = new Socket(wssconfig);
    this.SocketAgent.ioSocket.auth = { email };
    this.initAgentListeners();
    //[Listener] : Agent Socket connect
    this.SocketAgent.on('connect', async () => {
      //JOIN IN ROOMS
      // El join va ANTES de consultar el estado: el servidor responde con un broadcast
      // a la sala del asesor, asi que preguntar antes de unirse hace que la respuesta
      // se pierda y el widget de disponibilidad quede en "cargando" indefinidamente.
      this.joinAgentRooms(this._authService.user.company.id);
      //SET SESSION STATUS AGENT IF EMPTY LOCALSTORAGE
      if (this._authService.user.role.id === 5) {
        if (!this.activeAgentStatus) this.agentStatus({ emailAgent: this._authService.user.email, agentId: this._authService.user.id });
        // Only poll current status on the very first connect. Reconnects must not send id=5
        // because it races with any in-progress or recently completed status change.
        if (!this._hasConnectedOnce) {
          this._hasConnectedOnce = true;
          await this.setSessionStatus({ agentId: this._authService.user.id, sessionStatus: { id: 5, name: "undefined" } });
        }
      }
      this.loadTopics();
      this.refreshAllAvailable();
      // Notify subscribers (e.g. admin agent-detail view) that the socket (re)connected,
      // so they can re-join their reply room and re-query the resolved status.
      this._onConnect.next();
    });

    this.SocketAgent.on('disconnect', () => {
      this._agentSessionStatus.next({ id: 5, name: "undefined" });
      this._cachedAgent = null;
      this._cachedAgentId = null;
    });
  }


  //AGENT SOCKET EMITER
  emit(eventName: string, message: any): boolean {
    if (this.SocketAgent) {
      this.SocketAgent.emit(eventName, message);
      return true;
    }
    return false;
  }

  //AGENT SOCKET LISTENER
  listen(eventName: string) {
    if (this.SocketAgent) {
      return new Observable((subscriber) => {
        this.SocketAgent.on(eventName, (data) => {
          subscriber.next(data);
        });
      });
    }
  }

  /**
   * Agents functions
   * =====================
   * - addAgent(agent)
   * - updateAgent(agent)
   * - getAgent(agentId)
   * - getAgentList()
   * - deleteAgent(agentId)
   */
  //agentLogout
  agentLogout() {
    this.leaveAgentRooms(this._authService.user.company.id);
    this.changePushSession(0);
    const authUrl = `${environment.baseServerURL}/auth/logout`
    this.http.post(authUrl, {}, {
      withCredentials: true
    }).subscribe(
      async (_) => {
        this.localService.removeJsonValue('userData');
        this.localService.removeJsonValue('messages');
        this.localService.removeJsonValue('sessionId');
        this.localService.removeJsonValue('superAdminCompany');
        sessionStorage.clear();
        this._authService.user = {};
        this.router.navigate(['/login']);
        window.location.reload();
      },
      (error) => { console.error(error); });
  }

  setAgentOffline() {
    //Set offline
    if (this.SocketAgent && this._authService.user.role.id === 5) {
      this.sessionService.setOffline.subscribe(user => {
        this.getAgent(user.id).subscribe(async (agentResult) => {
          await this.setSessionStatus({ agentId: agentResult.id, sessionStatus: { id: 4, name: "offline" } })
          //Redirección a login...
          return this.agentLogout();
        });
      });
    }
  }

  changeCompany() {
    this.sessionService.changeCompany.subscribe(newCompany => {
      this.leaveAgentRooms(this.currentCompanyId);
      this.currentCompanyId = newCompany;
      this.joinAgentRooms(this.currentCompanyId);
      this._cachedAgent = null;
      this._cachedAgentId = null;
      this.loadTopics();
      this.refreshAllAvailable();
    })
  }

  joinAgentRooms(company) {
    // Init agent ROOMS
    this.emit('joinRoom', `${company}`);
    this.emit('joinRoom', `availableAgents-${company}`);
    this.emit('joinRoom', `allAvailableAgents-admin-${company}`);
    if (this._authService.user.role.id === 5) {
      this.emit('joinRoom', this.room);
    }
  }

  leaveAgentRooms(company) {
    // Leave agent ROOMS
    this.emit('leaveRoom', `${company}`);
    this.emit('leaveRoom', `availableAgents-${company}`);
    this.emit('leaveRoom', `allAvailableAgents-admin-${company}`);
    if (this._authService.user.role.id === 5) this.emit('leaveRoom', this.room);
  }

  initAgentListeners() {
    //ALL AGENT AVALABLE
    this.listen('getAllAvailableAgents')
      .subscribe((allAgentsList: any) => {
        this.AllAvailableAgentsList = [];
        allAgentsList.map((agentList, index) => {
          this.AllAvailableAgentsList[index] = [];
          this.AllAvailableAgentsList[index].push(agentList);
        });
      });

    //SET SESSION STATUS LISTENER
    this.listen('setSessionStatus')
      .subscribe(async (response: any) => {
        if (typeof response === 'object') {
          const previousId = this._agentSessionStatus.getValue().id;
          const incomingId = response.sessionStatus.id;
          this._agentSessionStatus.next(response.sessionStatus);
          if (previousId !== incomingId) {
            this.refreshAllAvailable();
            this.getAgentPermissions();
          }
        }
      });
  }

  changePushSession(status: number) {
    const token = this.localService.getJsonValue('authToken');
    if (token) this.sysNotification.changeActiveSession({ auth: token, status }).subscribe();
  }

  // Get contact used in send Message
  getContact(contactId: string) {
    const contactURL = `${environment.baseServerURL}/operation/${this._authService.user.company.id}/contact/${contactId}`;
    return this.http.get(contactURL, {
      withCredentials: true
    });
  }

  transferContact(conversationReference: any): Observable<any> {
    const transferURL = `${environment.baseServerURL}/operation/conversation/transfer`;
    return this.http.put(transferURL, conversationReference, {
      withCredentials: true
    });
  }

  closeConversation(conversationReference: any): Observable<any> {
    const closeURL = `${environment.baseServerURL}/operation/conversation/close`;
    return this.http.put(closeURL, conversationReference, {
      withCredentials: true
    });
  }

  sendMessage(sendMessage): Observable<any> {
    const sendURL = `${environment.baseServerURL}/operation/${this._authService.user.company.id}/sendMessageByChannel`;
    return this.http.post(sendURL, sendMessage, {
      withCredentials: true
    });
  }

  addAgent(agent): Observable<any> {
    return this.http.post(this.buildURL(''), agent, {
      withCredentials: true
    });
  }

  updateAgent(agent): Observable<any> {
    return this.http.put(this.buildURL(agent.id), agent, {
      withCredentials: true
    });
  }

  getAgent(agentId): Observable<any> {
    return this.http.get(this.buildURL(agentId + '/company/' + this._authService.user.company.id), {
      withCredentials: true
    });
  }

  getAgentList(): Observable<any> {
    return this.http.get(this.buildURL('company/' + this._authService.user.company.id), {
      withCredentials: true
    });
  }

  /**
   * Fuerza el estado offline de un agente (acción administrativa).
   * El body espera el estado como número plano, a diferencia del contrato
   * del socket (`AgentData.sessionStatus` es `{ id, name }`).
   * @param agentId - ID del agente a desconectar
   */
  public forceAgentOffline(agentId: number): Observable<any> {
    return this.http.put(this.buildURL(`${agentId}/status`), { sessionStatus: 4 }, {
      withCredentials: true
    });
  }

  deleteAgent(agentId): Observable<any> {
    return this.http.delete(this.buildURL('delete/' + agentId), {
      withCredentials: true
    });
  }

  /** Agent topics functions
   * =========================
   */
  createTopic(topic: any): Observable<any> {
    const url = `${this.ADMINISTRATION_URL}/${this._authService.user.company.id}/live/queue`;
    return this.http.post(url, topic, {
      withCredentials: true
    });
  }

  getGeneralQueue(): Observable<any> {
    const url = `${this.ADMINISTRATION_URL}/${this._authService.user.company.id}/live`;
    return this.http.get(url, {
      withCredentials: true
    });
  }

  getTopics(): Observable<any> {
    const url = `${this.ADMINISTRATION_URL}/${this._authService.user.company.id}/live/queue`;
    return this.http.get(url, {
      withCredentials: true
    });
  }

  // CON OPORTUNIDAD DE MEJORA: Optimizar esta función para evitar múltiples llamadas HTTP y mejorar el rendimiento
  getAgentTopics(agentId: number): Observable<any> {
    const url = `${this.URL}/user/company/${this._authService.user.company.id}/getAgentUser/${agentId}`;
    return this.http.get(url, {
      withCredentials: true
    });
  }

  updateTopic(topic: any): Observable<any> {
    const url = `${this.ADMINISTRATION_URL}/${this._authService.user.company.id}/live/queue/${topic.id}`;
    return this.http.put(url, topic, {
      withCredentials: true
    });
  }

  deleteTopic(id): Observable<any> {
    const url = `${this.ADMINISTRATION_URL}/${this._authService.user.company.id}/live/queue/${id}`;
    return this.http.delete(url, {
      withCredentials: true
    })
  }


  /**
   * Method for send agent status
   * @param {string} emailAgent - Cronjob name
   * @param {number} agentId - Agent id
   */
  private agentStatusTimer: any;
  private agentStatus({ emailAgent, agentId }: { emailAgent: string, agentId: number }) {
    this.worker = new Worker('../workers/agent-status.worker', { type: 'module' });
    this.emitAgentStatus({ emailAgent, agentId });

    this.worker.onmessage = ({ data }) => {
      this.activeAgentStatus = data;
    };

    this.worker.postMessage('Emit agent status');

    this.agentStatusTimer = setInterval(() => {
      this.emitAgentStatus({ emailAgent, agentId });
    }, 120000); // each two minutes
  }

  private emitAgentStatus({ emailAgent, agentId }: { emailAgent: string, agentId: number }) {
    this.emit('agentStatus', { emailAgent, agentId });
  }

  public getTotalActiveConversationsByAgent(agentId: number): Observable<any> {
    const path = `/getTotalActiveConversationsByAgent/${agentId}`;
    const url = `${environment.baseServerURL}/operation/${this._authService.user.company.id}${path}`;
    return this.http.get(url, {
      withCredentials: true
    });
  }

  public createNewConversation(body: { agentId: number, contactPhone: string }): Observable<any> {
    const requestURL = `${environment.baseServerURL}/operation/${this._authService.user.company.id}/createNewConversation`;
    return this.http.post(requestURL, body, {
      withCredentials: true
    });
  }

  public exportConversation(contactId) {
    const url = `${this.ADMINISTRATION_URL}/conversation/exportConversation`

    return new Observable(observer => {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contactId }),
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('Error en la respuesta del servidor');
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let chunks: Uint8Array[] | any = [];

          // Leer los fragmentos de datos (stream)
          const processStream = ({ done, value }) => {
            if (done) {
              const combinedBlob = new Blob(chunks, {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              });
              observer.next(combinedBlob);
              observer.complete();
              return;
            }

            // Decodifica los fragmentos de datos
            const decodedValue = decoder.decode(value, { stream: true });
            if (decodedValue && decodedValue.includes('PING')) {
              observer.next(decodedValue.replace(/^PING\s*/, ""))
            } else {
              chunks.push(value); // Acumula los fragmentos binarios
            }
            // Sigue procesando el stream
            return reader.read().then(processStream);
          };
          return reader.read().then(processStream);
        })
        .catch(err => {
          observer.error(err);
        });
    });
  }

  public get companyTopicList(): any[] {
    return this._companyTopicList.value;
  }

  public set companyTopicList(topicList: any[]) {
    this._companyTopicList.next(topicList);
  }

  /**
   * AGENT SESSION STATUS
   * setSessionStatus({ agentId, sessionStatus }): Establece el estado de sesión del agente y emite el cambio a través del socket
   * agentSessionStatus: Obtiene o establece el estado de sesión actual del agente
   * @param {number} agentId - ID del agente
   * @param {sessionStatus} sessionStatus - Nuevo estado de sesión del agente
   */
  public async setSessionStatus({ agentId, sessionStatus }: { agentId: number, sessionStatus: sessionStatus }): Promise<boolean> {
    try {
      if (!this._cachedAgent || this._cachedAgentId !== agentId) {
        this._cachedAgent = await this.getAgent(agentId).toPromise();
        this._cachedAgentId = agentId;
      }
    } catch (error) {
      console.error('[setSessionStatus] Error obteniendo datos del agente:', error);
      if (!this._cachedAgent) return false;
    }
    const agent = this._cachedAgent;
    const agentData: AgentData = {
      firstName: agent.firstName,
      sessionStatus: sessionStatus,
      role: agent.role,
      userId: agent.id,
      token: this._authService.user.token,
      email: agent.email,
      companyId: agent.companyId,
      queues: agent.queues || [],
    };
    return this.emit('sessionStatus', agentData);
  }

  public get agentSessionStatus(): sessionStatus {
    return this._agentSessionStatus.getValue();
  }

  public set agentSessionStatus(status: sessionStatus) {
    this._agentSessionStatus.next(status);
  }

  /**
   * FEATURE FLAGS
   * getAgentPermissions: Obtener los permisos del agente para funcionalidades específicas
   * get enableStartChatButton(): Obtener el valor actual de enableStartChatButton de forma síncrona
   * set enableStartChatButton(value: boolean): Establecer el valor de enableStartChatButton
   */
  private getAgentPermissions() {
    const _role = this._authService.user.role.id;
    const _userId = this._authService.user.id;
    if (_role === 5) {
      this.getAgent(_userId)
        .subscribe(agent => {
          if (agent) {
            const canStart = agent.canStartConversation;
            this._enableStartChatButton.next(canStart);
          }
        });
    }
  }

  /**
   * Obtener el valor actual de enableStartChatButton de forma síncrona
   */
  public get enableStartChatButton(): boolean {
    return this._enableStartChatButton.value;
  }

  /**
   * Establecer el valor de enableStartChatButton
   */
  public set enableStartChatButton(value: boolean) {
    this._enableStartChatButton.next(value);
  }
}
